import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import TripForm from "@/components/TripForm";
import { toast } from "sonner";
import { Truck, Plus, MapPin, ArrowRight, Building2, Package, Wallet, Navigation, CheckCircle2, ShieldAlert, Repeat, AlertTriangle, Bell, Pencil, Power, Trash2, Printer } from "lucide-react";
import { DocumentUpload } from "@/components/DocumentUpload";
import { BookingChat } from "@/components/BookingChat";
import { AdminClaims } from "@/components/AdminClaims";
import { useData } from "@/pages/Admin";
import { PartnerPayouts } from "@/components/PartnerPayouts";
import { PartnerBookings } from "@/components/PartnerBookings";
import { AdminCalendar } from "@/components/AdminCalendar";
import { ShareRoute } from "@/components/ShareRoute";
import { fmtDate } from "@/lib/utils";

export default function PartnerPortal() {
  const { user } = useAuth();
  const [partner, setPartner] = useState(undefined); // undefined = loading, null = none
  const [trips, setTrips] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editProfile, setEditProfile] = useState(false);
  const [editTrip, setEditTrip] = useState(null);
  const [notifs, setNotifs] = useState([]);

  const load = async () => {
    if (!user) return;
    try {
      const p = await api.get("/partner/me");
      setPartner(p.data && p.data.company_name ? p.data : null);
      const [t, b, n] = await Promise.all([api.get("/trips/mine"), api.get("/bookings/mine"), api.get("/notifications")]);
      setTrips(t.data); setBookings(b.data); setNotifs(n.data);
    } catch { setPartner(null); }
  };
  useEffect(() => { load(); }, [user]); // eslint-disable-line

  const updateTrip = async (data) => {
    try { await api.put(`/trips/${editTrip.id}`, { ...data, owner_type: "partner" }); toast.success("Fahrt gespeichert."); setEditTrip(null); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const toggleTrip = async (t) => {
    try { const r = await api.post(`/trips/${t.id}/status?status=${t.status === "aktiv" ? "inaktiv" : "aktiv"}`); toast.success(r.data.status === "aktiv" ? "Fahrt aktiviert – öffentlich sichtbar." : "Fahrt deaktiviert."); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const deleteTrip = async (t) => {
    if (!window.confirm(`Fahrt ${t.start_ort} → ${t.ziel_ort} am ${fmtDate(t.abfahrt)} wirklich löschen?`)) return;
    try { await api.delete(`/trips/${t.id}`); toast.success("Fahrt gelöscht."); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  // Not logged in → marketing + register CTA
  if (!user) {
    return (
      <div className="eb-container py-16 max-w-3xl text-center">
        <span className="eb-badge bg-[#FF5A1F]/15 text-[#FF8A5F] mb-4"><Truck className="w-3.5 h-3.5" /> Für Transportunternehmen</span>
        <h1 className="font-head text-4xl font-bold">Freien Laderaum anbieten</h1>
        <p className="text-slate-400 mt-4 mb-8">Registriere dich als Transportpartner, veröffentliche deine Fahrten und fülle freie Kapazitäten. Automatisches Matching mit passenden Kundenanfragen.</p>
        <div className="flex gap-3 justify-center">
          <Link to="/registrieren?role=partner" className="eb-btn-primary" data-testid="partner-register-cta">Als Partner registrieren</Link>
          <Link to="/login" className="eb-btn-outline">Anmelden</Link>
        </div>
      </div>
    );
  }

  // Admins manage routes in the admin area, not as partners
  if (user.role === "admin") {
    return (
      <div className="eb-container py-16 max-w-2xl text-center" data-testid="partner-admin-redirect">
        <h1 className="font-head text-3xl font-bold">Partnerportal nicht für Admin-Konten</h1>
        <p className="text-slate-400 mt-4 mb-8">Als Administrator verwaltest du eigene Routen und Partner im Adminbereich. Eine Partner-Registrierung ist für Admin-Konten nicht möglich.</p>
        <Link to="/admin?tab=routes" className="eb-btn-primary" data-testid="partner-admin-to-routes">Zu „Unsere Routen“</Link>
      </div>
    );
  }

  const createTrip = async (data) => {
    try { const r = await api.post("/trips", { ...data, owner_type: "partner" }); toast.success(r.data.generated ? `Fahrt veröffentlicht – ${r.data.generated} weitere Termine automatisch angelegt.` : (r.data.status === "entwurf" ? "Fahrt als Entwurf gespeichert – wird nach Freigabe sichtbar." : "Fahrt veröffentlicht.")); setShowForm(false); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const registerPartner = async (data) => {
    try { await api.post("/partner/register", data); toast.success("Unternehmen registriert."); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const setTransport = async (id, status) => {
    try { await api.post(`/bookings/${id}/transport-status?status=${status}`); toast.success(`Status: ${status}`); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="eb-container py-12">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-8">
        <div><h1 className="font-head text-3xl font-bold">Transportpartner-Portal</h1><p className="text-slate-400 mt-1">Fahrten, Kapazitäten und Buchungen verwalten.</p></div>
        {partner && <div className="flex gap-2"><Link to="/partner/fahrer" className="eb-btn-outline" data-testid="partner-driver-view-btn"><Navigation className="w-4 h-4" /> Fahrer-Ansicht</Link><Link to="/partner/tagesplan" className="eb-btn-outline" data-testid="partner-day-sheet-btn"><Printer className="w-4 h-4" /> Tagesplan drucken</Link><button onClick={() => setShowForm(!showForm)} className="eb-btn-primary" data-testid="partner-new-trip-btn"><Plus className="w-4 h-4" /> Fahrt erstellen</button></div>}
      </div>

      {partner === null && (
        <div className="eb-card p-6 lg:p-8 max-w-3xl" data-testid="partner-register-form">
          <h2 className="font-head text-xl font-semibold mb-1 flex items-center gap-2"><Building2 className="w-5 h-5 text-[#FF5A1F]" /> Unternehmensdaten</h2>
          <p className="text-sm text-slate-400 mb-6">Einmalige Registrierung als Transportpartner.</p>
          <PartnerForm defaults={{ email: user.email }} onSubmit={registerPartner} />
        </div>
      )}

      {partner && (
        <>
          {partner.insurance_valid_until && (() => { const d = Math.ceil((new Date(partner.insurance_valid_until) - Date.now()) / 864e5); return (d < 0 || d <= 30) && (
            <div className={`rounded-xl border p-4 mb-6 flex items-start gap-3 ${d < 0 ? "border-red-500/40 bg-red-500/5" : "border-amber-500/40 bg-amber-500/5"}`} data-testid="partner-insurance-banner">
              <ShieldAlert className={`w-5 h-5 shrink-0 ${d < 0 ? "text-red-400" : "text-amber-400"}`} />
              <div className="text-sm">
                <div className={`font-head font-semibold ${d < 0 ? "text-red-300" : "text-amber-300"}`}>{d < 0 ? "Versicherungsnachweis abgelaufen" : `Versicherungsnachweis läuft in ${d} Tagen ab`}</div>
                {d < 0 && <div className="text-slate-400 mt-1">Deine Fahrten sind gesperrt, bis ein gültiger Versicherungsnachweis hinterlegt ist.</div>}
                <button onClick={() => setEditProfile(true)} className="eb-btn-outline !py-1.5 text-xs mt-2" data-testid="partner-insurance-update-btn">Nachweis aktualisieren</button>
              </div>
            </div>); })()}
          {partner.status !== "aktiv" && (
            <div className={`rounded-xl border p-5 mb-8 flex items-start gap-3 ${partner.status === "abgelehnt" ? "border-red-500/40 bg-red-500/5" : "border-amber-500/40 bg-amber-500/5"}`} data-testid="partner-status-banner">
              <ShieldAlert className={`w-5 h-5 shrink-0 ${partner.status === "abgelehnt" ? "text-red-400" : "text-amber-400"}`} />
              <div className="text-sm">
                {partner.status === "abgelehnt" ? (
                  <><div className="font-head font-semibold text-red-300">Freigabe abgelehnt</div><div className="text-slate-400 mt-1">Grund: {partner.reject_reason || "–"}. Bitte ergänze deine Angaben – wir prüfen erneut.</div></>
                ) : (
                  <><div className="font-head font-semibold text-amber-300">Prüfung läuft</div><div className="text-slate-400 mt-1">Wir prüfen Versicherung und Genehmigungen. Fahrten kannst du schon als Entwurf anlegen – sie werden nach Freigabe automatisch sichtbar.</div></>
                )}
                <button onClick={() => setEditProfile(!editProfile)} className="eb-btn-outline !py-1.5 text-xs mt-3" data-testid="partner-edit-profile-btn">Angaben & Nachweise bearbeiten</button>
              </div>
            </div>
          )}
          {editProfile && <div className="eb-card p-6 lg:p-8 mb-8"><h2 className="font-head text-xl font-semibold mb-5">Unternehmensdaten & Nachweise</h2><PartnerForm defaults={partner} onSubmit={async (d) => { await registerPartner(d); setEditProfile(false); }} /></div>}
          {showForm && <div className="eb-card p-6 lg:p-8 mb-8"><h2 className="font-head text-xl font-semibold mb-5">Neue Fahrt</h2><TripForm onSubmit={createTrip} onCancel={() => setShowForm(false)} /></div>}
          {editTrip && <div className="eb-card p-6 lg:p-8 mb-8" data-testid="partner-trip-edit-form"><h2 className="font-head text-xl font-semibold mb-5">Fahrt bearbeiten</h2><TripForm initial={editTrip} onSubmit={updateTrip} onCancel={() => setEditTrip(null)} submitLabel="Speichern" /></div>}

          {notifs.length > 0 && (
            <div className="eb-card p-5 mb-8" data-testid="partner-notifications">
              <h2 className="font-head text-lg font-semibold mb-3 flex items-center gap-2"><Bell className="w-4 h-4 text-[#FF5A1F]" /> Benachrichtigungen</h2>
              <div className="space-y-2">
                {notifs.slice(0, 5).map((n) => (
                  <div key={n.id} className="flex items-start gap-3 text-sm" data-testid={`partner-notif-${n.id}`}>
                    <Bell className="w-4 h-4 text-[#FF5A1F] mt-0.5 shrink-0" />
                    <div><span className="font-semibold text-slate-200">{n.title}</span> <span className="text-slate-500">· {fmtDate(n.created_at)}</span><div className="text-slate-400">{n.message}</div></div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <h2 className="font-head text-xl font-semibold mb-4 flex items-center gap-2"><Truck className="w-5 h-5 text-[#FF5A1F]" /> Meine Fahrten ({trips.length})</h2>
          <div className="grid md:grid-cols-2 gap-4 mb-10">
            {trips.length === 0 ? <p className="text-slate-400">Noch keine Fahrten veröffentlicht.</p> : trips.map((t) => (
              <div key={t.id} className="eb-card p-5" data-testid={`partner-trip-${t.id}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className={`eb-badge ${t.status==="aktiv"?"bg-emerald-500/15 text-emerald-400":t.status==="entwurf"?"bg-amber-500/15 text-amber-400":"bg-slate-700 text-slate-300"}`}>{t.insurance_blocked ? "gesperrt" : t.status}{t.pending_approval ? " · wartet auf Freigabe" : ""}{t.recurring && t.recurring_weekdays?.length ? " · Serie" : t.template_id ? " · aus Serie" : ""}</span>
                  <span className="mono text-emerald-400 text-sm">{t.freie_m3} m³ frei</span>
                </div>
                <div className="flex items-center gap-2 font-head font-semibold flex-wrap"><MapPin className="w-4 h-4 text-[#FF5A1F]" /> {t.start_ort} {(t.stops || []).map((s, i) => <span key={i} className="flex items-center gap-2 text-slate-400 text-sm"><ArrowRight className="w-3.5 h-3.5 text-slate-600" /> {s.ort}</span>)} <ArrowRight className="w-4 h-4 text-slate-600" /> {t.ziel_ort}</div>
                <div className="text-sm text-slate-400 mt-1">{fmtDate(t.abfahrt)} · {t.vehicle_type}</div>
                <div className="flex flex-wrap items-center gap-1.5 mt-3 pt-3 border-t border-slate-800">
                  <button onClick={() => { setEditTrip(t); setShowForm(false); }} className="eb-btn-ghost !px-2.5 !py-1.5 text-xs" data-testid={`partner-trip-edit-${t.id}`}><Pencil className="w-3.5 h-3.5" /> Bearbeiten</button>
                  {!t.insurance_blocked && (t.status === "aktiv"
                    ? <button onClick={() => toggleTrip(t)} className="eb-btn-ghost !px-2.5 !py-1.5 text-xs" data-testid={`partner-trip-toggle-${t.id}`}><Power className="w-3.5 h-3.5" /> Deaktivieren</button>
                    : <button onClick={() => toggleTrip(t)} disabled={!!t.pending_approval} title={t.pending_approval ? "Wird nach Freigabe automatisch aktiv" : ""} className="eb-btn-ghost !px-2.5 !py-1.5 text-xs text-emerald-400 disabled:opacity-40" data-testid={`partner-trip-toggle-${t.id}`}><Power className="w-3.5 h-3.5" /> Aktivieren</button>)}
                  <button onClick={() => deleteTrip(t)} className="eb-btn-ghost !px-2.5 !py-1.5 text-xs text-red-400" data-testid={`partner-trip-delete-${t.id}`}><Trash2 className="w-3.5 h-3.5" /> Löschen</button>
                  {t.status === "aktiv" && <span className="ml-auto flex items-center gap-1.5"><span className="text-xs text-slate-500">Teilen:</span><ShareRoute trip={t} /></span>}
                </div>
              </div>
            ))}
          </div>

          <h2 className="font-head text-xl font-semibold mb-4 flex items-center gap-2"><Bell className="w-5 h-5 text-[#FF5A1F]" /> Fahrten-Kalender</h2>
          <div className="eb-card p-4 mb-10" data-testid="partner-calendar-card"><AdminCalendar endpoint="/partner/calendar" editHref={null} testPrefix="partner" /></div>

          <h2 className="font-head text-xl font-semibold mb-4 flex items-center gap-2"><Package className="w-5 h-5 text-[#FF5A1F]" /> Buchungsübersicht</h2>
          <PartnerBookings meId={user.id} setTransport={setTransport} />

          <h2 className="font-head text-xl font-semibold mb-4 mt-10 flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-amber-400" /> Schadensmeldungen</h2>
          <AdminClaims useData={useData} endpoint="/claims/mine" updateBase="/partner/claims" readOnlyFor={() => false} />
          <h2 className="font-head text-xl font-semibold mb-4 mt-10 flex items-center gap-2"><Wallet className="w-5 h-5 text-[#FF5A1F]" /> Auszahlungen</h2>
          <PartnerPayouts />
        </>
      )}
    </div>
  );
}

function PartnerForm({ defaults, onSubmit }) {
  const [f, setF] = useState({ company_name: "", contact_person: "", email: "", phone: "", address: "", website: "", company_data: "", ust_id: "", insurance: "", permits: "", vehicle_data: "", documents: [], insurance_valid_until: "", ...Object.fromEntries(Object.entries(defaults || {}).filter(([k]) => ["company_name","contact_person","email","phone","address","website","company_data","ust_id","insurance","permits","vehicle_data","documents","insurance_valid_until"].includes(k) && defaults[k] != null)) });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const FIELDS = [["company_name","Firmenname *"],["contact_person","Ansprechpartner *"],["email","E-Mail *"],["phone","Telefon *"],["address","Adresse"],["website","Website"],["ust_id","USt-ID (optional)"],["insurance","Versicherung"],["permits","Transportgenehmigungen"],["vehicle_data","Fahrzeugdaten"]];
  return (
    <div className="grid sm:grid-cols-2 gap-4">
      {FIELDS.map(([k, l]) => (
        <div key={k} className={k==="vehicle_data"||k==="permits"?"sm:col-span-2":""}>
          <label className="eb-label">{l}</label>
          <input className="eb-input mt-1.5" value={f[k]} onChange={set(k)} data-testid={`partner-field-${k}`} />
        </div>
      ))}
      <div><label className="eb-label">Versicherungsnachweis gültig bis</label><input type="date" className="eb-input mt-1.5" value={(f.insurance_valid_until || "").slice(0, 10)} onChange={set("insurance_valid_until")} data-testid="partner-insurance-until" /></div>
      <div className="sm:col-span-2">
        <DocumentUpload documents={f.documents} onChange={(documents) => setF({ ...f, documents })} />
      </div>
      <div className="sm:col-span-2">
        <button onClick={() => onSubmit(f)} className="eb-btn-primary" data-testid="partner-register-submit">{defaults?.company_name ? "Speichern" : "Registrieren"}</button>
      </div>
    </div>
  );
}
